import React from "react";
import { Gauge, Info } from "lucide-react";

const COLOR_FOR_SCORE = (s) => {
  if (s >= 75) return "#00ff88";
  if (s >= 50) return "#a78bfa";
  if (s >= 25) return "#ffd60a";
  return "#94a3b8";
};

const LABEL_FOR_SCORE = (s) => {
  if (s >= 75) return "TRUSTED CONTRIBUTOR";
  if (s >= 50) return "ESTABLISHED";
  if (s >= 25) return "BUILDING REP";
  return "NEWCOMER";
};

/**
 * Trust score badge — single number 0–100 with breakdown bars.
 * Hidden on public profiles (private metric for the signed-in owner only).
 */
export default function TrustScoreBadge({ data }) {
  if (!data) return null;
  const { score = 0, breakdown = {} } = data;
  const color = COLOR_FOR_SCORE(score);
  const label = LABEL_FOR_SCORE(score);

  // Three signals: sigils, vault cards owned, games played.
  const rows = [
    { key: "sigils_earned", label: "Sigils earned",  max: 40 },
    { key: "vault_cards",   label: "Vault cards",    max: 40 },
    { key: "games_played",  label: "Games played",   max: 20 },
  ];

  return (
    <div
      data-testid="trust-score-badge"
      className="border p-5"
      style={{ borderColor: `${color}55`, background: `linear-gradient(135deg, ${color}1f, ${color}05), #0a0a0a` }}
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Gauge size={13} style={{ color }} />
            <div className="font-mono text-[10px] tracking-[0.25em]" style={{ color }}>
              // TRUST SCORE
            </div>
          </div>
          <div className="font-heading font-black text-5xl mt-1" style={{ color }}>
            {score}
            <span className="text-zinc-600 text-2xl">/100</span>
          </div>
          <div className="font-mono text-[10px] tracking-[0.25em] mt-2" style={{ color }}>
            {label}
          </div>
        </div>
        <div className="text-zinc-500 text-xs max-w-[220px] leading-relaxed flex items-start gap-1.5">
          <Info size={11} className="mt-0.5 shrink-0" />
          <span>
            Earned, not given. Sourced comments and verified witness statements weight the most.
            Visible only to you.
          </span>
        </div>
      </div>
      <div className="space-y-2 pt-3 border-t border-white/5">
        {rows.map((r) => {
          const b = breakdown[r.key] || { value: 0, points: 0 };
          const pct = Math.min(100, Math.round((b.points / r.max) * 100));
          return (
            <div key={r.key} className="flex items-center gap-3" data-testid={`trust-row-${r.key}`}>
              <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-400 w-32 sm:w-44 truncate shrink-0">
                {r.label}
              </div>
              <div className="flex-1 h-1.5 bg-white/5 ">
                <div className="h-full  transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
              <div className="font-mono text-[10px] text-zinc-400 w-16 text-right shrink-0">
                {b.value} · +{b.points}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
